import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { ITipoPagamento } from "interfaces/payment";
import logoTnv from "assets/images/logo-tnv.png";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  TAXAS_ACCENT,
} from "../taxas.theme";

const switchLabelSx = {
  mx: 0,
  alignItems: "center",
  gap: 1,
  "& .MuiFormControlLabel-label": {
    fontSize: "0.875rem",
    lineHeight: 1.4,
  },
};

type Props = {
  open: boolean;
  row: ITipoPagamento | null;
  saving?: boolean;
  onClose: () => void;
  onSave: () => void;
  onChange: (field: keyof ITipoPagamento, value: unknown) => void;
};

export default function TaxaPagamentoModal({
  open,
  row,
  saving,
  onClose,
  onSave,
  onChange,
}: Props) {
  if (!row) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <Box
              component="img"
              src={row.icon}
              alt=""
              sx={{
                width: 56,
                height: 56,
                objectFit: "contain",
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                p: 0.5,
              }}
              onError={(e) => {
                e.currentTarget.src = logoTnv;
              }}
            />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Editar taxa
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {row.tipoPagamento}
              </Typography>
              <Box mt={0.75}>
                <Chip
                  size="small"
                  label={row.status ? "Ativo" : "Inativo"}
                  sx={{
                    bgcolor: row.status ? "rgba(16, 185, 129, 0.9)" : "rgba(239, 68, 68, 0.85)",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PaymentsOutlinedIcon sx={{ color: TAXAS_ACCENT }} />
            <MDTypography variant="button" fontWeight="bold">
              Configuração da taxa
            </MDTypography>
          </Stack>

          <TextField
            label="Tipo de pagamento"
            fullWidth
            size="small"
            disabled
            value={row.tipoPagamento}
            sx={fieldSx}
          />

          <TextField
            label="Taxa de transação"
            fullWidth
            size="small"
            type="number"
            value={row.taxa_transacao ?? ""}
            onChange={(e) => onChange("taxa_transacao", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {row.taxaIsPercentage ? "%" : "R$"}
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <FormControlLabel
            sx={switchLabelSx}
            control={
              <Switch
                checked={row.taxaIsPercentage}
                onChange={(_, checked) => onChange("taxaIsPercentage", checked)}
              />
            }
            label="Taxa em porcentagem"
          />

          <TextField
            label="URL do ícone"
            fullWidth
            size="small"
            value={row.icon ?? ""}
            onChange={(e) => onChange("icon", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box
                    component="img"
                    src={row.icon}
                    alt=""
                    sx={{ height: 32, width: "auto" }}
                    onError={(e) => {
                      e.currentTarget.src = logoTnv;
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={fieldSx}
          />

          <FormControlLabel
            sx={switchLabelSx}
            control={
              <Switch
                checked={row.status}
                onChange={(_, checked) => onChange("status", checked)}
              />
            }
            label="Forma de pagamento ativa"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={saving}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={onSave}
          disabled={saving}
          sx={{
            background: `linear-gradient(195deg, ${TAXAS_ACCENT}, #e04a1a)`,
          }}
        >
          {saving ? "Salvando…" : "Salvar alterações"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
